import React, { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Paper,
  useMediaQuery,
  alpha,
  Stack,
  Divider,
  IconButton,
  SwipeableDrawer,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import OwnerStripeBalancesWidget from '../financial/OwnerStripeBalancesWidget';
import OwnerCashBalancesWidget from '../financial/OwnerCashBalancesWidget';

const PRIMARY = '#6200ee';
const PRIMARY_DARK = '#4b00b5';
const SECONDARY = '#03dac4';

const BalancesPanel = ({ variant = 'compact' }) => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation('dashboard');
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDrawerMode = variant === 'drawer';

  if (!isAuthenticated) return null;

  /* ─── Shared widgets content ─────────────────────────────────── */
  const widgetsContent = (
    <Stack spacing={2}>
      <Box
        sx={{
          borderRadius: '10px',
          overflow: 'hidden',
          border: `1px solid ${alpha(PRIMARY, 0.2)}`,
          backgroundColor: alpha(PRIMARY, 0.04),
        }}
      >
        <OwnerStripeBalancesWidget />
      </Box>
      <Divider
        sx={{
          borderImage: `linear-gradient(90deg, transparent, ${alpha(PRIMARY, 0.25)}, ${alpha(SECONDARY, 0.25)}, transparent) 1`,
        }}
      />
      <Box
        sx={{
          borderRadius: '10px',
          overflow: 'hidden',
          border: `1px solid ${alpha(SECONDARY, 0.3)}`,
          backgroundColor: alpha(SECONDARY, 0.04),
        }}
      >
        <OwnerCashBalancesWidget />
      </Box>
    </Stack>
  );

  /* ─── Shared solid header ───────────────────────────────────── */
  const panelHeader = (onClose) => (
    <Box
      sx={{
        backgroundColor: PRIMARY,
        color: 'white',
        px: 2.5,
        py: 1.75,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: -20,
          right: -20,
          width: 110,
          height: 110,
          background: `radial-gradient(circle, ${alpha('#ffffff', 0.12)} 0%, transparent 70%)`,
          borderRadius: '50%',
          pointerEvents: 'none',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 1 }}>
        <WalletIcon />
        <Box>
          <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
            {t('balances.title', 'Mes Soldes')}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.82, display: 'block' }}>
            {t('balances.subtitle', 'Virement et Cash')}
          </Typography>
        </Box>
      </Box>
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          color: 'white',
          position: 'relative',
          zIndex: 1,
          '&:hover': { backgroundColor: alpha('#fff', 0.18) },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  /* ─── Shared Mobile Bottom Sheet ────────────────────────────── */
  const mobileBottomSheet = (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      disableSwipeToOpen
      sx={{ zIndex: 1400 }}
      PaperProps={{
        sx: {
          width: '100vw',
          maxWidth: '100vw',
          left: 0,
          right: 0,
          margin: 0,
          borderRadius: '20px 20px 0 0',
          maxHeight: '85vh',
          height: '85vh',
          overflow: 'hidden',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 0.5, flexShrink: 0 }}>
        <Box
          sx={{
            width: 44,
            height: 5,
            backgroundColor: alpha(PRIMARY, 0.25),
            borderRadius: 3,
          }}
        />
      </Box>

      <Box sx={{ flexShrink: 0 }}>
        {panelHeader(() => setOpen(false))}
      </Box>

      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          p: { xs: 1.5, sm: 2.5 },
          overflowY: 'auto',
          flex: 1,
          '&::-webkit-scrollbar': { width: '5px' },
          '&::-webkit-scrollbar-thumb': {
            background: alpha(PRIMARY, 0.25),
            borderRadius: '10px',
          },
        }}
      >
        {widgetsContent}
      </Box>
    </SwipeableDrawer>
  );

  /* ─── MOBILE header bar (standalone button) ──────────────────── */
  if (isMobile && !isDrawerMode) {
    return (
      <>
        <Button
          onClick={() => setOpen(true)}
          variant="outlined"
          size="small"
          startIcon={<WalletIcon />}
          sx={{
            fontWeight: 600,
            borderColor: PRIMARY,
            color: PRIMARY,
            '&:hover': {
              borderColor: PRIMARY_DARK,
              backgroundColor: alpha(PRIMARY, 0.08),
            },
          }}
        >
          {t('balances.button', 'Soldes')}
        </Button>
        {mobileBottomSheet}
      </>
    );
  }

  /* ─── DRAWER sidebar mode (button opening bottom sheet) ──────── */
  if (isDrawerMode) {
    return (
      <Box>
        <Button
          onClick={() => setOpen(true)}
          fullWidth
          size="large"
          startIcon={<WalletIcon />}
          sx={{
            fontWeight: 600,
            justifyContent: 'flex-start',
            borderRadius: '8px',
            border: `1px solid ${PRIMARY}`,
            color: open ? 'white' : PRIMARY,
            backgroundColor: open ? PRIMARY : 'transparent',
            py: 1,
            transition: 'all 0.25s ease',
            '&:hover': {
              backgroundColor: open ? PRIMARY_DARK : alpha(PRIMARY, 0.08),
            },
          }}
        >
          {t('balances.button', 'Soldes')}
        </Button>
        {mobileBottomSheet}
      </Box>
    );
  }

  /* ─── DESKTOP dropdown (seamless connection to button) ────────── */
  return (
    <Box sx={{ position: 'relative', zIndex: 1200 }}>
      <Button
        onClick={() => setOpen(!open)}
        size="small"
        startIcon={<WalletIcon />}
        endIcon={
          <ExpandMoreIcon
            sx={{
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.25s ease',
            }}
          />
        }
        sx={{
          minWidth: 130,
          px: 1.75,
          py: 0.75,
          fontWeight: 600,
          borderRadius: open ? '8px 8px 0 0' : '8px',
          border: `1px solid ${PRIMARY}`,
          borderBottom: open ? `1px solid ${PRIMARY}` : `1px solid ${PRIMARY}`,
          color: open ? 'white' : PRIMARY,
          backgroundColor: open ? PRIMARY : alpha(PRIMARY, 0.04),
          transition: 'all 0.25s ease',
          position: 'relative',
          zIndex: 1202,
          '&:hover': {
            backgroundColor: open ? PRIMARY : alpha(PRIMARY, 0.1),
            borderColor: PRIMARY,
            boxShadow: open ? 'none' : `0 4px 14px ${alpha(PRIMARY, 0.22)}`,
          },
        }}
      >
        {t('balances.button', 'Soldes')}
      </Button>

      {/* Dropdown panel — sits flush below button via mt: -1px overlap */}
      <Collapse
        in={open}
        timeout="auto"
        unmountOnExit={false}
        sx={{
          position: 'absolute',
          top: '100%',
          right: 0,
          width: 820,
          mt: '-1px',
          zIndex: 1201,
        }}
      >
        <Paper
          elevation={12}
          sx={{
            borderRadius: '8px 0 8px 8px',
            overflow: 'hidden',
            border: `1px solid ${PRIMARY}`,
            borderTop: `1px solid ${PRIMARY}`,
            boxShadow: `0 12px 40px ${alpha(PRIMARY, 0.22)}, 0 4px 12px ${alpha(PRIMARY_DARK, 0.1)}`,
          }}
        >
          {panelHeader(() => setOpen(false))}

          {/* Scrollable content area */}
          <Box
            sx={{
              maxHeight: 'calc(80vh - 72px)',
              overflowY: 'auto',
              p: 2.5,
              backgroundColor: '#fff',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': {
                background: alpha(PRIMARY, 0.06),
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: alpha(PRIMARY, 0.22),
                borderRadius: '10px',
                '&:hover': { background: alpha(PRIMARY, 0.4) },
              },
            }}
          >
            {widgetsContent}
          </Box>
        </Paper>
      </Collapse>

      {/* Backdrop to close on outside click */}
      {open && (
        <Box
          onClick={() => setOpen(false)}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1150,
            backgroundColor: 'transparent',
          }}
        />
      )}
    </Box>
  );
};

export default BalancesPanel;
